import React from 'react';
import { connect } from 'react-redux';

import {
  workflowRemoveFinalizeApproval,
  workflowRemoveFinalizeApprovalCount,
  workflowSetFinalizeApprovalCount,
  workflowSetFinalizeApprovalOrder,
  workflowSetFinalizeTaskApproval,
  workflowSetFinalizeTaskApprovalDescription,
  workflowSetFinalizeTaskApprovalTitle,
  workflowToggleFinalizeApprovalType,
  workflowToggleFinalizeConditionRule,
} from '~/actions/workflow';
import ActionsMenu from '~/components/Shared/ActionsMenu/ActionsMenu';
import { hasValidApprovalValue } from '~/components/Workflow/Workflow.utils';
import { WorkflowReviewFinalizeApprovalType } from '~/enums/WorkflowReviewFinalizeApprovalType';
import { useUsers } from '~/hooks/useUsers';
import { useIntl } from 'react-intl';
import { getFinalizeTaskById } from '~/reducers/workflow';
import Box from '~/ui/components/Box/Box';
import EditableText from '~/ui/components/EditableText/EditableText';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import NumberInput from '~/ui/components/NumberInput/NumberInput';
import RadioGroup from '~/ui/components/RadioGroup/RadioGroup';
import Text from '~/ui/components/Text/Text';
import { useModal, useModalSortable } from '~/ui/hooks/modal';
import { toSnakeTrimmed } from '~/utils/strings';
import { coerceName } from '~/utils/workflow';

import ConditionRuleInput from '../../../shared/ConditionRuleInput';
import DescriptionTextAreaForm from '../../../shared/DescriptionTextAreaForm';
import WorkflowUserDepartmentMultiSelect from '../../../shared/WorkflowUserDepartmentMultiSelect';

const formSegmentNested = {
  mb: 5,
  backgroundColor: 'black-alpha-04',
  borderRadius: 'm',
};

function PhaseItem({
  phaseId,
  approval,
  index,
  canReorder,
  //connected
  disabled,
  workflow,
  workflowRemoveFinalizeApproval,
  workflowSetFinalizeTaskApproval,
  workflowSetFinalizeTaskApprovalTitle,
  workflowSetFinalizeTaskApprovalDescription,
  workflowToggleFinalizeConditionRule,
  workflowToggleFinalizeApprovalType,
  workflowSetFinalizeApprovalCount,
  workflowRemoveFinalizeApprovalCount,
  workflowSetFinalizeApprovalOrder,
}) {
  const intl = useIntl();
  const { id } = approval;
  const finalizeApprovals =
    getFinalizeTaskById(workflow, phaseId)?.approvals || [];
  const [sortableListModal, showSortableListModal] = useModalSortable({
    confirm: (sortedItems) =>
      workflowSetFinalizeApprovalOrder({
        orders: sortedItems.map((sortedItem) => sortedItem.value),
        phaseId,
      }),
    items: finalizeApprovals.map((item, index) => ({
      value: item.id,
      label: coerceName(
        item.name,
        intl.formatMessage({
          id: 'PhaseItem.defaultFinalizeTaskName',
          defaultMessage: 'Finalize Task',
          description: 'Default name for a new finalize task.',
        }),
        index,
      ),
    })),
    text: intl.formatMessage({
      id: 'PhaseItem.sortText',
      defaultMessage: 'Sort',
      description: 'Sort text',
    }),
    title: intl.formatMessage({
      id: 'PhaseItem.reorderFinalizePhasesTitle',
      defaultMessage: 'Reorder Finalize Phases',
      description: 'Modal title for reordering finalize phases',
    }),
  });

  const users = useUsers();

  const [deleteModal, showDeleteModal] = useModal({
    actionButton: {
      variant: 'red',
      text: intl.formatMessage({
        id: 'PhaseItem.removeButton',
        defaultMessage: 'Remove',
        description: 'Remove button text',
      }),
      errorHandler: () => {},
      promise: async () =>
        await workflowRemoveFinalizeApproval({ id, phaseId }),
    },
    title: intl.formatMessage({
      id: 'PhaseItem.removeApprovalTitle',
      defaultMessage: 'Remove Approval?',
      description: 'Modal title for removing approval confirmation',
    }),
    content: (
      <Text color="gray-900" variant="s-spaced">
        {intl.formatMessage({
          id: 'PhaseItem.removeApprovalMessage',
          defaultMessage: 'Are you sure you want to remove the approval?',
          description: 'Confirmation message for removing approval',
        })}{' '}
        <Text variant="s-spaced-bold">
          {intl.formatMessage({
            id: 'PhaseItem.undoWarning',
            defaultMessage: "You won't be able to undo this action.",
            description: 'Warning message about irreversible action',
          })}
        </Text>
      </Text>
    ),
  });

  const coercedTaskName = coerceName(
    approval.name,
    intl.formatMessage({
      id: 'PhaseItem.defaultFinalizeTaskName',
      defaultMessage: 'Finalize Task',
      description: 'Default name for a new finalize task.',
    }),
    index,
  );
  const taskName = toSnakeTrimmed(coercedTaskName);

  const getMenuItems = () => {
    const items = [];

    items.push({
      content: intl.formatMessage({
        id: 'PhaseItem.reorderFinalizePhases',
        defaultMessage: 'Reorder Finalize Phases',
        description: 'Menu item for reordering finalize phases',
      }),
      disabled: !canReorder,
      onClick: () => showSortableListModal(),
    });

    items.push({
      content: intl.formatMessage({
        id: 'PhaseItem.removeFinalizeTask',
        defaultMessage: 'Remove Finalize Task',
        description: 'Menu item for removing finalize task',
      }),
      onClick: () => showDeleteModal(),
    });

    return items;
  };

  const hasUserDepartmentError = !hasValidApprovalValue(approval, users);

  return (
    <Box>
      <Box sx={formSegmentNested}>
        <Box sx={{ borderBottom: 'border' }}>
          <FlexLayout p={4} alignItems="center" justifyContent="space-between">
            <EditableText
              id={`workflow--finalize-edit-task-name=${taskName}-name`}
              placeholder={intl.formatMessage(
                {
                  id: 'PhaseItem.defaultFinalizeTaskNameAndNumber',
                  defaultMessage: `Finalize Task {taskNumber}`,
                  description: 'Placeholder for finalize task name',
                },
                { taskNumber: index + 1 },
              )}
              value={approval.name}
              onChange={(value) =>
                workflowSetFinalizeTaskApprovalTitle({ value, id, phaseId })
              }
            />
            <Box pl={4}>
              <ActionsMenu
                id={`workflow_builder_finalize_phase_actions_menu_id=${id}`}
                items={getMenuItems()}
                align="end"
              />
            </Box>
          </FlexLayout>
        </Box>
        <Box p={4}>
          <Box pt={2} pb={2} sx={{ borderBottom: 'border' }}>
            <DescriptionTextAreaForm
              description={approval.description}
              disabled={disabled}
              id={`workflow--finalize-edit-task-name=${taskName}-description`}
              onUpdate={(value) =>
                workflowSetFinalizeTaskApprovalDescription({
                  value,
                  id,
                  phaseId,
                })
              }
            />
          </Box>
          <Box pt={4} pb={4} mt={4} sx={{ borderBottom: 'border' }}>
            <ConditionRuleInput
              conditionRule={approval.conditionRule}
              id={`workflow--finalize-edit-task-name=${taskName}-condition-rule`}
              onChange={(conditionRule) =>
                workflowToggleFinalizeConditionRule({
                  conditionRule,
                  id,
                  phaseId,
                })
              }
            />
          </Box>
          <Box mt={4} mb={4}>
            <Text variant="xs-dense-caps">
              {intl.formatMessage({
                id: 'PhaseItem.finalizersLabel',
                defaultMessage: 'Finalizers',
                description: 'Label for finalizers section',
              })}
            </Text>
          </Box>
          <Box mb={4}>
            <WorkflowUserDepartmentMultiSelect
              error={
                hasUserDepartmentError
                  ? intl.formatMessage({
                      id: 'PhaseItem.usersNotExistError',
                      defaultMessage:
                        "There are users that don't exist anymore",
                      description: 'Error message for non-existent users',
                    })
                  : ''
              }
              id={`workflow--finalize-edit-task-name=${taskName}-finalizers`}
              value={approval.approvers}
              onChange={(updatedApprovers) => {
                if (!updatedApprovers.length) {
                  workflowRemoveFinalizeApprovalCount({ id, phaseId });
                }
                workflowSetFinalizeTaskApproval({
                  approvers: updatedApprovers,
                  id,
                  phaseId,
                });
              }}
            />
          </Box>

          <Box mt={4}>
            <Text variant="xs-dense-caps">
              {intl.formatMessage({
                id: 'PhaseItem.assignToLabel',
                defaultMessage: 'Assign to',
                description: 'Label for assign to section',
              })}
            </Text>
            <RadioGroup
              id={`workflow--finalize-edit-task-name=${taskName}-assign-to`}
              inline={true}
              label={intl.formatMessage({
                id: 'PhaseItem.requireApprovalFromLabel',
                defaultMessage: 'Require Approval From',
                description: 'Require Approval text',
              })}
              value={approval.approvalType}
              options={[
                {
                  label: intl.formatMessage({
                    id: 'PhaseItem.everyoneOption',
                    defaultMessage: 'Everyone',
                    description:
                      'Approve option for requiring approval from everyone',
                  }),
                  value: WorkflowReviewFinalizeApprovalType.Everyone,
                },
                {
                  label: intl.formatMessage({
                    id: 'PhaseItem.specificNumberOptionFinalizers',
                    defaultMessage: 'Specific number of finalizers',
                    description:
                      'Option for requiring a specific number of finalizers',
                  }),
                  value: WorkflowReviewFinalizeApprovalType.Specific,
                },
              ]}
              onChange={(approvalType) =>
                workflowToggleFinalizeApprovalType({
                  approvalType,
                  id,
                  phaseId,
                })
              }
            />
          </Box>

          {approval.approvalType !==
            WorkflowReviewFinalizeApprovalType.Everyone && (
            <FlexLayout alignItems="baseline" space={3} mt={4}>
              <Text
                color={!approval.approvers.length && 'gray-500'}
                variant="xs-dense"
              >
                {intl.formatMessage({
                  id: 'PhaseItem.numberOfFinalizersText',
                  defaultMessage: 'Number of finalizers to assign this to',
                  description: 'Label for number of finalizers input',
                })}
              </Text>
              <NumberInput
                disabled={!approval.approvers.length}
                id={`workflow--finalize-edit-task-name=${taskName}-number-finalizers`}
                value={approval.minApprovalCount}
                min={1}
                onChange={(minApprovalCount) =>
                  workflowSetFinalizeApprovalCount({
                    minApprovalCount,
                    id,
                    phaseId,
                  })
                }
              />
            </FlexLayout>
          )}
        </Box>
      </Box>
      {sortableListModal}
      {deleteModal}
    </Box>
  );
}

function mapStateToProps({ builder, workflow }) {
  return {
    disabled: !builder.shouldEnableAllComponents,
    workflow,
  };
}

export default connect(mapStateToProps, {
  workflowRemoveFinalizeApproval,
  workflowSetFinalizeTaskApproval,
  workflowSetFinalizeTaskApprovalTitle,
  workflowSetFinalizeTaskApprovalDescription,
  workflowToggleFinalizeConditionRule,
  workflowToggleFinalizeApprovalType,
  workflowSetFinalizeApprovalCount,
  workflowRemoveFinalizeApprovalCount,
  workflowSetFinalizeApprovalOrder,
})(PhaseItem);
